import type { PushSubscriptionInput } from '@/contract'
import { partnerApi } from '@/lib/api'

/* Spec PARTNER-002 D-03 / D-10 / D-11 (copia do admin-app, ADMIN-002) — avisos neste aparelho (navegador), sem React.
   A inscricao e do navegador: o servidor so guarda o endereco. Cada abertura do app repete o PUT (mantem o servidor
   em dia se o navegador trocou a inscricao). Sair apaga a inscricao no servidor e no navegador antes da sessao acabar. */

export type DeviceSupport = 'supported' | 'unsupported' | 'needs-install'
export type DeviceState = 'denied' | 'off' | 'on'

const SW_URL = '/sw.js'

function isIos(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function isInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

/** iPhone/iPad so recebem avisos com o app instalado na tela de inicio (US-001 aceite 6). */
export function deviceSupport(): DeviceSupport {
    if (isIos() && !isInstalled()) return 'needs-install'
    const ok = window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
    return ok ? 'supported' : 'unsupported'
}

/** Registro do service worker (main.tsx registra na carga; aqui garante, sem esperar para sempre). */
async function registration(): Promise<ServiceWorkerRegistration> {
    return (await navigator.serviceWorker.getRegistration('/')) ?? navigator.serviceWorker.register(SW_URL)
}

async function currentSubscription(): Promise<PushSubscription | null> {
    if (deviceSupport() !== 'supported') return null
    const reg = await navigator.serviceWorker.getRegistration('/')
    return reg ? reg.pushManager.getSubscription() : null
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
    const raw = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    const out = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
    return out
}

function sameKey(subscription: PushSubscription, publicKey: string): boolean {
    const current = subscription.options.applicationServerKey
    if (!current) return true
    const a = new Uint8Array(current)
    const b = keyBytes(publicKey)
    return a.length === b.length && a.every((v, i) => v === b[i])
}

function toInput(subscription: PushSubscription): PushSubscriptionInput {
    const json = subscription.toJSON()
    return { endpoint: subscription.endpoint, keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' } }
}

/** Situacao deste aparelho e, se ligado, repete o PUT no servidor (D-02). Chave trocada no servidor: desliga aqui. */
export async function syncDevice(publicKey: string): Promise<DeviceState> {
    if (Notification.permission === 'denied') return 'denied'
    const subscription = await currentSubscription()
    if (!subscription || Notification.permission !== 'granted') return 'off'
    if (!sameKey(subscription, publicKey)) {
        await subscription.unsubscribe().catch(() => false)
        return 'off'
    }
    await partnerApi.pushSubscribe(toInput(subscription))
    return 'on'
}

/** Ligar (no toque da pessoa: o pedido de permissao precisa vir de um gesto). */
export async function enableDevice(publicKey: string): Promise<DeviceState> {
    const permission = await Notification.requestPermission()
    if (permission === 'denied') return 'denied'
    if (permission !== 'granted') return 'off'
    const reg = await registration()
    let subscription = await reg.pushManager.getSubscription()
    // Inscricao feita com outra chave (par VAPID trocado no servidor): o navegador so aceita outra depois de cancelar
    if (subscription && !sameKey(subscription, publicKey)) {
        await subscription.unsubscribe().catch(() => false)
        subscription = null
    }
    subscription ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) })
    try {
        await partnerApi.pushSubscribe(toInput(subscription))
    } catch (error) {
        // Servidor recusou (servico de push desconhecido, fora do ar): nao deixa inscricao solta no navegador
        await subscription.unsubscribe().catch(() => false)
        throw error
    }
    return 'on'
}

/** Desligar neste aparelho. */
export async function disableDevice(): Promise<DeviceState> {
    const subscription = await currentSubscription()
    if (subscription) {
        await partnerApi.pushUnsubscribe(subscription.endpoint)
        await subscription.unsubscribe().catch(() => false)
    }
    return Notification.permission === 'denied' ? 'denied' : 'off'
}

export async function testDevice(): Promise<void> {
    const subscription = await currentSubscription()
    if (!subscription) throw new Error('no-subscription')
    await partnerApi.pushTest(subscription.endpoint)
}

/** Sair (D-11): apaga a inscricao deste aparelho antes de encerrar a sessao. Nunca segura a saida por mais de 3 s. */
export async function releaseDevice(): Promise<void> {
    const work = (async () => {
        const subscription = await currentSubscription()
        if (!subscription) return
        await partnerApi.pushUnsubscribe(subscription.endpoint).catch(() => undefined)
        await subscription.unsubscribe().catch(() => false)
    })().catch(() => undefined)
    await Promise.race([work, new Promise(resolve => setTimeout(resolve, 3000))])
}
